import { get } from "lodash";

import {
  auth0UserMetadataKey,
  getApiUrlFromAuth0,
  getFromUserOrAuth,
  isInternalSupportUser,
  isInternalTestUser,
} from "src/services/auth";
import { getActiveTenant } from "src/services/localStorage";

/**
 * Logout runs from `Auth0Provider`, which wraps the router above `TenantApolloProvider`.
 * On some screens (e.g. global error / "Be right back") there is no Apollo client in scope,
 * and we must still clear impersonation before `completeLogout()` wipes storage. So we call
 * `setSupportImpersonation` with `getTokenSilently` + `fetch` instead of `useMutation`.
 *
 * Do not copy this pattern elsewhere — prefer Apollo under `TenantApolloProvider`. This path
 * exists only for support/test users and this pre-logout hook.
 */

const CLEAR_SUPPORT_IMPERSONATION_MUTATION = `mutation clearSupportImpersonation {
  clearSupportImpersonation {
    success
  }
}`;

const hasPersistedImpersonation = (rawUser) => {
  const imp = get(rawUser, [auth0UserMetadataKey, "impersonation"]);
  if (!imp || typeof imp !== "object" || Array.isArray(imp)) {
    return false;
  }
  const hasEmail = typeof imp.email === "string" && imp.email.trim().length > 0;
  const hasRoles = Array.isArray(imp.roles) && imp.roles.length > 0;
  const hasWorkspaceRoles =
    Array.isArray(imp.workspaceRoles) && imp.workspaceRoles.length > 0;
  return hasEmail || hasRoles || hasWorkspaceRoles;
};

/**
 * Clears support email + role impersonation in Auth0 `user_metadata` via the existing GraphQL
 * mutation, before local/session storage is wiped on logout.
 *
 * @param {import("@auth0/auth0-spa-js").Auth0Client | null | undefined} auth0Client
 * @returns {Promise<void>}
 */
export const clearSupportImpersonationBeforeLogout = async (auth0Client) => {
  if (!auth0Client) {
    return;
  }

  const rawUser = await auth0Client.getUser();
  if (!rawUser) {
    return;
  }

  const isSupportOrTestUser =
    isInternalSupportUser(rawUser) || isInternalTestUser(rawUser);
  if (!isSupportOrTestUser || !hasPersistedImpersonation(rawUser)) {
    return;
  }

  let token;
  try {
    token = await auth0Client.getTokenSilently();
  } catch {
    return;
  }
  if (!token) {
    return;
  }

  const userSub = rawUser.sub;
  const overrideTenant = userSub ? getActiveTenant(userSub) : undefined;
  const tenantId =
    get(overrideTenant, "tenantId") ?? getFromUserOrAuth(rawUser, "tenantId");
  const apiUrlFromClaims = getFromUserOrAuth(rawUser, "apiUrl");
  const apiBase = getApiUrlFromAuth0(
    apiUrlFromClaims,
    tenantId,
    overrideTenant
  );
  const uri = `${apiBase}/graphql`;

  const body = JSON.stringify({
    operationName: "clearSupportImpersonation",
    query: CLEAR_SUPPORT_IMPERSONATION_MUTATION,
  });

  try {
    await fetch(uri, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body,
    });
  } catch {
    // Network failure — still proceed with logout.
  }
};
